import { useEffect, useState } from 'react';

import { buildAddressLabel } from '@forms/autoAddress/AutoAddressHelper';
import { FormFieldError } from '@forms/FormFieldError/FormFieldError';
import {
    GetPostcodeLookupResponse,
    fetchGetPostcodeLookup
} from '@iwoca/lapi-client/edge';
import {
    Button,
    ComboBox,
    ComboBoxElement,
    ComboBoxOption,
    Input,
    Spinner
} from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import {
    FieldErrorsImpl,
    UseFormGetValues,
    UseFormRegister,
    UseFormSetValue
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import messages from '../MSHForm.messages';
import { TFormValues } from '../MSHForm.types';
import styles from '../MSHModal.module.css';

const AddressFields = ({
    errors,
    register,
    getValues,
    setValue,
    disabled = false
}: {
    errors: FieldErrorsImpl<TFormValues>;
    register: UseFormRegister<TFormValues>;
    getValues: UseFormGetValues<TFormValues>;
    setValue: UseFormSetValue<TFormValues>;
    disabled?: boolean;
}) => {
    type TPostcodeAddress = GetPostcodeLookupResponse['data'][number];

    const intl = useIntl();
    const [postcodeSearchTerm, setPostcodeSearchTerm] = useState('');
    const [debouncedPostcodeSearchTerm, setDebouncedPostcodeSearchTerm] =
        useState('');
    const [isManuallyEnteringAddress, setIsManuallyEnteringAddress] =
        useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedPostcodeSearchTerm(postcodeSearchTerm);
        }, 500);

        return () => clearTimeout(timeout);
    }, [postcodeSearchTerm]);

    // Get addresses
    const { data: addresses, isLoading: addressesLoading } = useQuery({
        queryKey: ['postcode_lookup', debouncedPostcodeSearchTerm],
        queryFn: async () => {
            try {
                const res = await fetchGetPostcodeLookup({
                    query: { postcode: debouncedPostcodeSearchTerm }
                });
                return res;
            } catch (e) {
                console.error(e);
                return { data: [] };
            }
        },

        enabled: debouncedPostcodeSearchTerm !== '',
        select: (res) => res!.data
    });

    const onChange = (value: TPostcodeAddress) => {
        if (!value) return;

        if (value.house_name) {
            setValue('address.house_name', value.house_name, {
                shouldValidate: true
            });
        }

        if (value.house_number) {
            setValue('address.house_number', value.house_number, {
                shouldValidate: true
            });
        }

        if (value.street_line_1) {
            setValue('address.street_line_1', value.street_line_1, {
                shouldValidate: true
            });
        }

        if (value.street_line_2) {
            setValue('address.street_line_2', value.street_line_2, {
                shouldValidate: true
            });
        }

        if (value.town) {
            setValue('address.town', value.town, {
                shouldValidate: true
            });
        }

        if (value.postcode) {
            setValue('address.postcode', value.postcode, {
                shouldValidate: true
            });
        }

        if (value.ptc_abs_code) {
            setValue('address.equifax_token', value.ptc_abs_code);
        }
    };

    return (
        <div className={`${styles.fields} ${styles.addressFields}`}>
            <h3 className={styles.addressFieldsTitle}>
                <FormattedMessage {...messages.addressFieldsTitle} />
            </h3>

            <div className={styles.addressList}>
                <div className={styles.address}>
                    {isManuallyEnteringAddress ? (
                        <div className={styles.addressManualFields}>
                            <div className={styles.houseNumber}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        {...messages.houseNumberFieldLabel}
                                    />
                                </label>

                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.house_number')}
                                    placeholder={intl.formatMessage(
                                        messages.houseNumberFieldPlaceholder
                                    )}
                                    disabled={disabled}
                                />
                            </div>

                            <div className={styles.houseName}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        {...messages.houseNameFieldLabel}
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.house_name')}
                                    placeholder={intl.formatMessage(
                                        messages.houseNameFieldPlaceholder
                                    )}
                                    disabled={disabled}
                                />
                            </div>

                            <div className={styles.addressManualField}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        {...messages.streetLineOneFieldLabel}
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.street_line_1')}
                                    placeholder={intl.formatMessage(
                                        messages.streetLineOneFieldPlaceholder
                                    )}
                                    disabled={disabled}
                                />
                            </div>
                            <div className={styles.addressManualField}>
                                <label className={styles.label}>
                                    <FormattedMessage
                                        {...messages.streetLineTwoFieldLabel}
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.street_line_2')}
                                    placeholder={intl.formatMessage(
                                        messages.streetLineTwoFieldPlaceholder
                                    )}
                                    disabled={disabled}
                                />
                            </div>

                            <div className={`${styles.field} ${styles.town}`}>
                                {errors.address?.town && (
                                    <FormFieldError
                                        message={messages.validTownRequired}
                                    />
                                )}
                                <label className={styles.label}>
                                    <FormattedMessage
                                        {...messages.townFieldLabel}
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.town', {
                                        required: true
                                    })}
                                    placeholder={intl.formatMessage(
                                        messages.townFieldPlaceholder
                                    )}
                                    disabled={disabled}
                                />
                            </div>
                            <div className={styles.postcode}>
                                {errors.address?.postcode && (
                                    <FormFieldError
                                        message={messages.validPostcodeRequired}
                                    />
                                )}
                                <label
                                    className={styles.label}
                                    htmlFor="postcode"
                                >
                                    <FormattedMessage
                                        {...messages.postcodeManualFieldLabel}
                                    />
                                </label>
                                {/* eslint-disable-next-line iwoca/input-a11y */}
                                <Input
                                    {...register('address.postcode', {
                                        required: true
                                    })}
                                    placeholder={intl.formatMessage(
                                        messages.postcodeManualFieldPlaceholder
                                    )}
                                    id="postcode"
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className={`${styles.addressSearchWrap} `}>
                            {errors.address?.postcode && (
                                <FormFieldError
                                    className={styles.addressSearchError}
                                    message={messages.validAddressRequired}
                                />
                            )}

                            <ComboBox
                                {...register('address.postcode', {
                                    required: true
                                })}
                                className={styles.addressSearchCombobox}
                                onQueryChange={(val) => {
                                    setPostcodeSearchTerm(val);
                                }}
                                onOptionSelect={(value) => {
                                    onChange(JSON.parse(value));
                                }}
                                value={buildAddressLabel(getValues('address'))}
                                placeholder={intl.formatMessage(
                                    messages.postcodeFieldPlaceholder
                                )}
                                disabled={disabled}
                            >
                                {!postcodeSearchTerm && (
                                    <ComboBoxElement>
                                        <FormattedMessage
                                            {...messages.emptyPostcodeError}
                                        />
                                    </ComboBoxElement>
                                )}

                                {postcodeSearchTerm && addressesLoading && (
                                    <ComboBoxElement>
                                        <Spinner />
                                    </ComboBoxElement>
                                )}

                                {postcodeSearchTerm &&
                                    addresses?.length === 0 && (
                                        <ComboBoxElement>
                                            <FormattedMessage
                                                {...messages.noMatchingPostcodesError}
                                            />
                                        </ComboBoxElement>
                                    )}

                                {postcodeSearchTerm &&
                                    addresses &&
                                    addresses.map((address, index) => (
                                        <ComboBoxOption
                                            key={`${address.postcode}-${index}`}
                                            value={JSON.stringify(address)}
                                        >
                                            {buildAddressLabel(address)}
                                        </ComboBoxOption>
                                    ))}
                            </ComboBox>
                        </div>
                    )}
                    <Button
                        variant="tertiary"
                        onClick={() =>
                            setIsManuallyEnteringAddress((prev) => !prev)
                        }
                        type="button"
                        disabled={disabled}
                    >
                        {isManuallyEnteringAddress ? (
                            <FormattedMessage
                                {...messages.toggleEnterPostcodeCTA}
                            />
                        ) : (
                            <FormattedMessage
                                {...messages.toggleManualAddressCTA}
                            />
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default AddressFields;
